import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { DataLoaderResult } from '../../../services/data-loader/data-loader-result';
import { standardRun } from '../../../services/data-loader/utils/standard-run';
import { DashboardGcpBillingSqlMap } from '../sql-maps/dashboard-gcp-billing-sql-map';

export interface GcpBillingDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

export class GcpBillingDataLoaderFactory extends Factory<
    DataLoader,
    GcpBillingDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'GcpBillingDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(options: GcpBillingDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getSalesAnalyticsVsStoreFactory] Provided options are of type SalesAnalyticsVsStoreDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceColumns = queryContext.datasourceColumns;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    datasourceColumns,
                    DashboardGcpBillingSqlMap.sqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { splitSelect, splitGroupBy, where, dateTrunc } =
                    DashboardGcpBillingSqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const query = `
    -- DataLoaderId: ${this.instanceId}

SELECT DATE(${dateTrunc}) AS date,
  ${splitSelect}

  -- metrics
  ROUND(SUM(cost_gross_eur), 2) AS cost_gross_eur,
  ROUND(SUM(cost_gross_usd), 2) AS cost_gross_usd,
  ROUND(SUM(cost_gross_pln), 2) AS cost_gross_pln
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    DashboardGcpBillingSqlMap.sqlMap.dataset,
                    DashboardGcpBillingSqlMap.sqlMap.tableId
                )}\`
${where}
GROUP BY ${splitGroupBy}date
ORDER BY ${splitGroupBy}date
    `;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                var data = [...queryResponse.result];
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
